import React from "react";
import { CheckCircle2, Circle, CircleDot } from "lucide-react";
import { useLang } from "../lib/i18n";

/** Guided path of a derived report: what is done, what is next, what is still missing. */
export function ReportSteps({ r, narr }) {
  const { t } = useLang();
  if (!r) return null;
  const hasRows = (r.indicators || []).some((x) => x.current || x.previous);
  const lecture = (narr?.lecture || "").trim();
  const lectureDone = lecture && !lecture.includes("(À compléter par l'auteur");
  const narrDone = (r.narrative_groups || []).some((g) => (narr?.[g.code] || "").trim());
  const st = r.status || "draft";
  const submitted = ["submitted", "validated", "consolidated"].includes(st);
  const validated = ["validated", "consolidated"].includes(st);
  const steps = [
    { k: "entries", label: "Saisies du terrain", done: (r.entries_count || 0) > 0, hint: `${r.entries_count || 0} ${t("saisie(s) agrégée(s)")}` },
    { k: "generated", label: "Génération", done: true, hint: (r.generated_at || "").slice(0, 10) },
    { k: "check", label: "Vérification indicateurs & tableau de bord", done: hasRows, hint: hasRows ? t("Chiffres disponibles") : t("Aucune valeur saisie") },
    { k: "lecture", label: "Lecture de la situation", done: !!lectureDone, hint: lectureDone ? t("Expliquée") : t("Compléter l'explication du terrain") },
    { k: "narrative", label: "Analyse narrative", done: narrDone, hint: narrDone ? t("Rédigée") : t("À rédiger (copilote disponible)") },
    { k: "submit", label: "Soumission", done: submitted, hint: submitted ? t("Soumis") : st === "returned" ? t("Renvoyé : corriger puis re-soumettre") : t("Enregistrer puis soumettre") },
    { k: "validate", label: "Validation du superviseur", done: validated, hint: validated ? `${t("Validé par")} ${r.signed_by || ""}` : t("En attente") },
  ];
  const next = steps.find((s) => !s.done);
  return (
    <section className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-4 mb-4" data-testid="report-steps">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-slate-900">{t("Parcours du rapport")}</h2>
        {next ? <span className="text-xs text-[#F05A28] font-semibold" data-testid="report-steps-next">{t("Prochaine étape")} : {t(next.label)}</span>
          : <span className="text-xs text-emerald-700 font-semibold" data-testid="report-steps-next">{t("Rapport complet et validé")}</span>}
      </div>
      <ol className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
        {steps.map((s, i) => {
          const current = next && next.k === s.k;
          const Icon = s.done ? CheckCircle2 : current ? CircleDot : Circle;
          return (
            <li key={s.k} className={`rounded-md border p-2.5 ${s.done ? "border-emerald-200 bg-emerald-50/50" : current ? "border-[#F05A28] bg-[#FFF3EE]" : "border-slate-200"}`} data-testid={`report-step-${s.k}`}>
              <div className="flex items-center gap-1.5">
                <Icon className={`h-4 w-4 shrink-0 ${s.done ? "text-emerald-600" : current ? "text-[#F05A28]" : "text-slate-300"}`} />
                <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{i + 1}</span>
              </div>
              <p className="text-xs font-semibold text-slate-800 mt-1 leading-tight">{t(s.label)}</p>
              <p className="text-[11px] text-slate-500 mt-0.5 leading-tight">{s.hint}</p>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

export default ReportSteps;
